const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/**
 * Dates always read as "05 Sep 2026".
 *
 * Never slashes. In a right-to-left paragraph a bare "05/09/2026" gets
 * reordered by the browser and day and month swap places, which on a page of
 * money is not a cosmetic problem. A written-out month cannot be misread.
 */
export function formatDate(iso: string | undefined | null): string {
  if (!iso) return "";
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return iso;
  return `${String(d).padStart(2, "0")} ${MONTHS[m - 1]} ${y}`;
}

/**
 * The clock time an entry was written, as a shopkeeper reads a clock.
 *
 * <p>Twelve hour with صبح / دوپہر / شام, because nobody at a counter says
 * "fifteen twenty". Shown in the phone's own timezone, which is the shop's
 * timezone on the phone that is standing in the shop.
 */
export function formatTime(iso: string | undefined | null): string {
  if (!iso) return "";
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";

  const hours = at.getHours();
  const minutes = String(at.getMinutes()).padStart(2, "0");
  const part =
    hours < 12 ? "صبح" : hours < 16 ? "دوپہر" : hours < 19 ? "شام" : "رات";
  const twelve = hours % 12 === 0 ? 12 : hours % 12;

  return `${twelve}:${minutes} ${part}`;
}

/**
 * When an entry was written, said next to the day it is written against.
 *
 * <p>Usually the two are the same day and the clock alone is the answer. They
 * are not always: a sale is often dated to the day the goods went out and typed
 * up that evening, or the next morning. Showing a bare "11:12 رات" beside a date
 * it does not belong to invites exactly the wrong conclusion about when
 * something happened, so on those rows the day is spelled out too.
 */
export function formatEnteredAt(
  iso: string | undefined | null,
  against: string | undefined | null,
): string {
  if (!iso) return "";
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";

  const day = `${at.getFullYear()}-${String(at.getMonth() + 1).padStart(2, "0")}-${String(
    at.getDate(),
  ).padStart(2, "0")}`;

  const clock = formatTime(iso);
  return day === against?.slice(0, 10) ? clock : `${formatDate(day)}، ${clock}`;
}

/** Today, as the API wants it. */
export function today(): string {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
}

export function monthStart(iso = today()): string {
  return `${iso.slice(0, 7)}-01`;
}

export function addDays(iso: string, days: number): string {
  const date = new Date(`${iso}T00:00:00`);
  date.setDate(date.getDate() + days);
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
}

/**
 * Whole rupees with thousands separators.
 *
 * Money arrives from the API as a string and is only ever parsed here, at the
 * last moment before it is shown. Nothing is ever calculated from the parsed
 * value - the server already did the arithmetic and rounded it once.
 */
export function formatMoney(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "—";
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toLocaleString("en-US", { maximumFractionDigits: 0 });
}

/** Weights keep three decimals but drop trailing zeroes: 1.2 kg, not 1.200 kg. */
export function formatWeight(
  value: string | number | null | undefined,
): string {
  if (value === null || value === undefined || value === "") return "—";
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toLocaleString("en-US", { maximumFractionDigits: 3 });
}

export function isNegative(value: string | number | null | undefined): boolean {
  if (value === null || value === undefined) return false;
  return Number(value) < 0;
}

/**
 * Turns what a shopkeeper types into a number the API will accept.
 * Accepts Urdu-Indic digits, because a tablet keyboard set to Urdu produces
 * them and refusing the entry would be the app being difficult for no reason.
 */
export function parseNumberInput(raw: string): string {
  const urduDigits = "۰۱۲۳۴۵۶۷۸۹";
  const arabicDigits = "٠١٢٣٤٥٦٧٨٩";

  let out = "";
  for (const ch of raw.trim()) {
    const urdu = urduDigits.indexOf(ch);
    const arabic = arabicDigits.indexOf(ch);
    if (urdu >= 0) out += String(urdu);
    else if (arabic >= 0) out += String(arabic);
    else if (/[0-9.]/.test(ch)) out += ch;
  }
  return out;
}

/**
 * A filename somebody can find again in a downloads folder six weeks later.
 *
 * Latin script and no spaces: an Urdu filename is legal but arrives mangled
 * through half the apps a bill passes through, and a space becomes %20 in a
 * WhatsApp caption.
 */
export function billFilename(name: string, from?: string, to?: string): string {
  const slug =
    name
      .trim()
      .replace(/[^\w\u0600-\u06FF]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 40) || "bill";

  const span =
    from || to ? `-${from || "start"}-to-${to || today()}` : `-${today()}`;
  return `wajid-flower-${slug}${span}`;
}

/**
 * A big figure shortened to something a glance can hold.
 *
 * <p>A headline of 2,185,262 is seven digits nobody reads as a quantity - they
 * read the first two and the length. Shown as «21.9 لاکھ» it is three
 * characters and the exact rupees go underneath, which is the way round a
 * number this size wants to be read.
 *
 * <p>لاکھ and کروڑ rather than million, because that is how the figure is said
 * out loud in the shop. Below a لاکھ nothing is gained by shortening, so it is
 * left exact.
 */
export function formatShort(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "—";
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return String(value);

  const sign = n < 0 ? "-" : "";
  const size = Math.abs(n);

  if (size >= 10_000_000) return `${sign}${trim(size / 10_000_000)} کروڑ`;
  if (size >= 100_000) return `${sign}${trim(size / 100_000)} لاکھ`;
  return formatMoney(n);
}

/** One decimal, and not even that when it would be a nought. */
function trim(value: number): string {
  const rounded = value.toFixed(1);
  return rounded.endsWith(".0") ? rounded.slice(0, -2) : rounded;
}
